import React, { useEffect, useRef, useState } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { googleProvider } from "../firebase";
import { FcGoogle } from "react-icons/fc";
import { IoEyeOffOutline, IoEyeOutline, IoClose } from "react-icons/io5";
import {
  doc,
  setDoc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const THEME = {
  bg1: "#0d0f14",
  card: "rgba(20, 24, 34, 0.7)",
  stroke: "rgba(255,255,255,0.08)",
  neon1: "#ff2d55", // neon-magenta
  neon2: "#00d1ff", // neon-cyan
  text: "#e8ecf1",
  sub: "rgba(232,236,241,0.7)",
  dangerBg: "rgba(255,45,85,0.1)",
  danger: "#ff2d55",
};

const AuthModal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);

  const cardRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (cardRef.current && !cardRef.current.contains(e.target)) onClose();
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      if (activeTab === "login") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const result = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        await saveUserProfileIfNeeded(result.user);
      }
      onClose();
    } catch (err) {
      console.log(err.message);
      showMappedError(err.code);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await saveUserProfileIfNeeded(result.user);
      onClose();
    } catch (err) {
      console.log(err.message);
      showMappedError(err.code);
    } finally {
      setLoading(false);
    }
  };

  const showMappedError = (code) => {
    setError(mapFirebaseError(code));
    setShowError(true);
    setTimeout(() => setShowError(false), 4000);
  };

  const mapFirebaseError = (code) => {
    switch (code) {
      case "auth/invalid-email":
        return "Invalid email format.";
      case "auth/user-disabled":
        return "This user account has been disabled.";
      case "auth/user-not-found":
        return "No account found with this email.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/email-already-in-use":
        return "An account with this email already exists.";
      case "auth/weak-password":
        return "Password should be at least 6 characters.";
      case "auth/popup-closed-by-user":
        return "Google sign-in popup was closed before completing.";
      case "auth/invalid-credential":
        return "Invalid login credentials. Please try again.";
      default:
        return "An unexpected error occurred. Please try again.";
    }
  };

  const saveUserProfileIfNeeded = async (firebaseUser) => {
    const userRef = doc(db, "users", firebaseUser.uid);
    const publicRef = doc(db, "public_profiles", firebaseUser.uid);

    const userSnap = await getDoc(userRef);
    const publicSnap = await getDoc(publicRef);

    const defaultProfileURL =
      "https://firebasestorage.googleapis.com/v0/b/gtafanpage-8e42a.firebasestorage.app/o/default_profile.png?alt=media&token=84a2ee8e-7ae0-4aa9-b55f-db71c81bbd81";
    const STARTING_BADGE = "Rookie";
    const STARTING_LEVEL = STARTING_BADGE;
    if (!userSnap.exists()) {
      const displayName = firebaseUser.displayName || "User";
      const firstName = displayName.split(" ")[0];

      await setDoc(userRef, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        photoURL: firebaseUser.photoURL || defaultProfileURL,
        userName: displayName,
        role: "member",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (!publicSnap.exists()) {
        await setDoc(publicRef, {
          uid: firebaseUser.uid,
          photoURL: firebaseUser.photoURL || defaultProfileURL,
          displayName: firstName,
          xp: 0,
          level: STARTING_LEVEL,
          badges: [STARTING_BADGE],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    } else {
      if (!publicSnap.exists()) {
        await setDoc(publicRef, {
          uid: firebaseUser.uid,
          photoURL: firebaseUser.photoURL || defaultProfileURL,
          displayName: "",
          xp: 0,
          level: STARTING_LEVEL,
          badges: [STARTING_BADGE],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
  };

  return (
    <div style={styles.overlay} onMouseDown={handleBackdropClick}>
      {/* Background neon gradient orbs */}
      <div style={{ ...styles.orb, ...styles.orbA }} />
      <div style={{ ...styles.orb, ...styles.orbB }} />

      <div
        ref={cardRef}
        style={styles.card}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} aria-label="Close" style={styles.closeBtn}>
          <IoClose size={22} />
        </button>

        <div style={styles.header}>
          <div style={styles.logoWrap}>
            <span style={styles.logoBadge}>GTA</span>
            <span style={styles.logoText}>FanPage</span>
          </div>
          <p style={styles.subhead}>
            Join the community. Post, comment & react in real time.
          </p>
        </div>

        <div style={styles.tabs}>
          <button
            style={activeTab === "login" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("login")}
          >
            Login
          </button>
          <button
            style={activeTab === "signup" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("signup")}
          >
            New Registration
          </button>
          <span
            style={{
              ...styles.activeUnderline,
              transform:
                activeTab === "login" ? "translateX(0%)" : "translateX(100%)",
            }}
          />
        </div>

        {error && (
          <div
            style={{
              ...styles.errorBox,
              opacity: showError ? 1 : 0,
              transform: showError ? "translateY(0)" : "translateY(-6px)",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleAuth} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            placeholder="you@email.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            autoComplete="email"
          />

          <label style={styles.label}>Password</label>
          <div style={styles.pwWrap}>
            <input
              type={showPw ? "text" : "password"}
              placeholder="Enter password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ ...styles.input, paddingRight: "44px" }}
              autoComplete={
                activeTab === "login" ? "current-password" : "new-password"
              }
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Hide password" : "Show password"}
              style={styles.eyeBtn}
            >
              {showPw ? (
                <IoEyeOffOutline size={18} />
              ) : (
                <IoEyeOutline size={18} />
              )}
            </button>
          </div>

          <button type="submit" style={styles.primaryBtn} disabled={loading}>
            {loading
              ? "Please wait…"
              : activeTab === "login"
              ? "Login"
              : "Create Account"}
          </button>
        </form>

        <div style={styles.divider}>
          <span style={styles.line} />
          <span style={styles.dividerText}>or</span>
          <span style={styles.line} />
        </div>

        <button
          onClick={handleGoogleSignIn}
          style={styles.googleBtn}
          disabled={loading}
        >
          <FcGoogle size={20} />
          <span>{loading ? "Connecting…" : "Continue with Google"}</span>
        </button>

        <p style={styles.terms}>
          By continuing, you agree to our <a style={styles.link}>Terms</a> and{" "}
          <a style={styles.link}>Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
};

/* ---------- Styles (inline for simplicity) ---------- */
const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background:
      "radial-gradient(1200px 700px at 20% 10%, rgba(255,45,85,0.08), transparent 60%), radial-gradient(900px 600px at 80% 90%, rgba(0,209,255,0.07), transparent 60%), rgba(3,7,14,0.85)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 3000,
    padding: "24px",
  },
  orb: {
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(40px)",
    opacity: 0.6,
    pointerEvents: "none",
  },
  orbA: {
    width: 220,
    height: 220,
    left: "8%",
    top: "12%",
    background: THEME.neon1,
    opacity: 0.12,
  },
  orbB: {
    width: 280,
    height: 280,
    right: "10%",
    bottom: "10%",
    background: THEME.neon2,
    opacity: 0.12,
  },
  card: {
    position: "relative",
    width: "min(420px, 92vw)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
    border: `1px solid ${THEME.stroke}`,
    boxShadow:
      "0 10px 30px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.02)",
    borderRadius: 16,
    overflow: "hidden",
    padding: "22px 22px 18px",
    color: THEME.text,
  },
  closeBtn: {
    position: "absolute",
    top: 10,
    right: 10,
    background: "transparent",
    color: THEME.sub,
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10,
    width: 36,
    height: 36,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
  },
  header: { paddingTop: 6, paddingBottom: 6 },
  logoWrap: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    userSelect: "none",
  },
  logoBadge: {
    background: "linear-gradient(135deg, #ff2d55, #ff7a00 50%, #ffd500 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    fontSize: 22,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  logoText: {
    background: `linear-gradient(135deg, ${THEME.neon2}, #9effa9)`,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: 0.4,
  },
  subhead: {
    marginTop: 6,
    fontSize: 13,
    color: THEME.sub,
  },
  tabs: {
    marginTop: 12,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0,
    position: "relative",
    background: "rgba(255,255,255,0.04)",
    borderRadius: 12,
    border: `1px solid ${THEME.stroke}`,
    overflow: "hidden",
  },
  tab: {
    padding: "10px 12px",
    background: "transparent",
    border: "none",
    color: THEME.sub,
    fontWeight: 700,
    cursor: "pointer",
  },
  tabActive: {
    padding: "10px 12px",
    background:
      "linear-gradient(135deg, rgba(0,209,255,0.22), rgba(255,45,85,0.22))",
    border: "none",
    color: THEME.text,
    fontWeight: 800,
    cursor: "pointer",
    textShadow: "0 0 12px rgba(0,209,255,0.35)",
  },
  activeUnderline: {
    position: "absolute",
    bottom: -1,
    left: 0,
    width: "50%",
    height: 2,
    background: "linear-gradient(90deg, #00d1ff, #ff2d55)",
    transition: "transform 220ms ease",
  },
  errorBox: {
    backgroundColor: THEME.dangerBg,
    color: THEME.danger,
    padding: "10px 12px",
    borderRadius: 10,
    textAlign: "center",
    border: `1px solid rgba(255,45,85,0.2)`,
    marginTop: 12,
    transition: "all 240ms ease",
  },
  form: { marginTop: 14 },
  label: {
    display: "block",
    fontSize: 12,
    color: THEME.sub,
    marginBottom: 6,
    fontWeight: 600,
  },
  input: {
    width: "100%",
    marginBottom: "14px",
    padding: "12px 12px",
    background: "rgba(0,0,0,0.35)",
    color: THEME.text,
    borderRadius: 12,
    outline: "none",
    border: `1px solid ${THEME.stroke}`,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
  },
  pwWrap: { position: "relative" },
  eyeBtn: {
    position: "absolute",
    right: 10,
    top: 0,
    bottom: 0,
    margin: "auto",
    height: 32,
    width: 32,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background: "transparent",
    color: THEME.sub,
    border: "none",
    cursor: "pointer",
  },
  primaryBtn: {
    width: "100%",
    padding: "12px 14px",
    background: "linear-gradient(135deg, #00d1ff, #8a5cff 50%, #ff2d55)",
    color: "#0d0f14",
    border: "none",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer",
    boxShadow:
      "0 8px 20px rgba(0,209,255,0.25), 0 0 0 1px rgba(255,255,255,0.06) inset",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "14px 0",
  },
  dividerText: {
    fontSize: 12,
    color: THEME.sub,
    textTransform: "uppercase",
    letterSpacing: 1.2,
  },
  line: {
    height: 1,
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    flex: 1,
  },
  googleBtn: {
    width: "100%",
    padding: "11px 14px",
    background: "rgba(255,255,255,0.9)",
    color: "#222",
    border: "none",
    borderRadius: 12,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.6rem",
  },
  terms: {
    marginTop: 10,
    fontSize: 11.5,
    color: THEME.sub,
    textAlign: "center",
  },
  link: {
    color: THEME.text,
    textDecoration: "underline",
    cursor: "pointer",
  },
};

export default AuthModal;
